import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom'
import Footer from '../../component/Footer';
import Header from '../../component/Header';
import { addToCart } from "../../action";
import './Cartpage.css';
import dayjs from 'dayjs';
import axios from "../../helper/axios";

import { useDispatch, useSelector } from "react-redux";

const Cartpage = () => {
    const dispatch = useDispatch();
    const cartData = useSelector(state => state.cart?.cartItems)
    const [qty, setQty] = useState(1);
    const [productPriceData, setProductPriceData] = useState({}); // Store fresh price data for display only

    const cart = useSelector((state) => state.cart);
    const auth = useSelector(state => state?.productData?.users);

    // Gjen SKU sipas skuid (item_code) që është shtuar në shportë – çmimi merret për atë skuid
    const findSkuBySkuId = (productDetails, skuId) => {
        if (!productDetails || !skuId) return null;
        const id = String(skuId).trim();
        for (const variant of productDetails?.variants || []) {
            for (const size of variant?.sizes || []) {
                const sku = (size?.skus || []).find(s => String(s?.skuId || '').trim() === id);
                if (sku) return sku;
            }
        }
        return null;
    };

    // Nxjerr price/discountPrice/discountPrice2/fromDate/toDate nga SKU (model ka prices[] ose fushat në sku)
    const getSkuPriceData = (sku) => {
        if (!sku) return {};
        const p = (sku.prices && sku.prices[0]) ? sku.prices[0] : sku;
        return {
            price: p?.price ?? sku?.listPrice,
            discountPrice: p?.discountPrice ?? sku?.discountPrice,
            discountPrice2: p?.discountPrice2 ?? sku?.discountPrice2,
            fromDate: p?.fromDate ?? sku?.fromDate,
            toDate: p?.toDate ?? sku?.toDate,
            fromDate2: p?.fromDate2 ?? sku?.fromDate2,
            toDate2: p?.toDate2 ?? sku?.toDate2
        };
    };

    const parsePossibleMongoDate = (d) => {
        if (!d) return null;
        if (typeof d === "string") return dayjs(d);
        if (d instanceof Date) return dayjs(d);
        if (d?.$date) return dayjs(d.$date);
        return dayjs(d);
    };

    const inRange = (now, start, end) => {
        const s = start ? parsePossibleMongoDate(start)?.startOf("day") : null;
        const e = end ? parsePossibleMongoDate(end)?.endOf("day") : null;
        if (s && s.isValid() && now.isBefore(s)) return false;
        if (e && e.isValid() && now.isAfter(e)) return false;
        return true;
    };

    // Derive latest regular/sale price for this cart item (based on DB SKU data)
    const repriceItemFromProductDetails = (it, productDetails) => {
        const sku = findSkuBySkuId(productDetails, it?.item_code);
        const pd = getSkuPriceData(sku);
        const regular = Number((pd?.price ?? sku?.listPrice ?? it?.regular_price) || 0);

        const now = dayjs();
        const discount = Number(pd?.discountPrice || 0);
        const discount2 = Number(pd?.discountPrice2 || 0);

        const has2 = discount2 > 0 && inRange(now, pd?.fromDate2 ?? pd?.fromDate, pd?.toDate2 ?? pd?.toDate);
        const has1 = !has2 && discount > 0 && inRange(now, pd?.fromDate, pd?.toDate);
        const sale = has2 ? discount2 : (has1 ? discount : 0);

        return { regular_price: regular, sale_price: sale };
    };

    const getProductDetailsForItem = (item) => {
        const productId = item?._id || item?.externalId;
        const loc = item?.location || "";
        return (
            productPriceData[`${productId}::${loc}`] ||
            productPriceData[productId] ||
            null
        );
    };

    // Helper function to check if discount is currently active (display only - does not affect order calculations)
    const isDiscountActive = (item) => {
        const productDetails = getProductDetailsForItem(item);
        const sku = findSkuBySkuId(productDetails, item?.item_code);
        const priceData = getSkuPriceData(sku);
        if (priceData && Object.keys(priceData).length) {
            const discount = Number(priceData?.discountPrice) || 0;
            const discount2 = Number(priceData?.discountPrice2) || 0;
            if ((!discount || discount <= 0) && (!discount2 || discount2 <= 0)) return false;

            const parsePossibleMongoDate = (d) => {
                if (!d) return null;
                if (typeof d === "string") return dayjs(d);
                if (d instanceof Date) return dayjs(d);
                if (d?.$date) return dayjs(d.$date);
                return dayjs(d);
            };

            const now = dayjs();
            const start = priceData?.fromDate ? parsePossibleMongoDate(priceData.fromDate).startOf("day") : null;
            const end = priceData?.toDate ? parsePossibleMongoDate(priceData.toDate).endOf("day") : null;
            const start2 = priceData?.fromDate2 ? parsePossibleMongoDate(priceData.fromDate2).startOf("day") : start;
            const end2 = priceData?.toDate2 ? parsePossibleMongoDate(priceData.toDate2).endOf("day") : end;

            const isValidRangeStart = start && start.isValid();
            const isValidRangeEnd = end && end.isValid();
            const isValidRangeStart2 = start2 && start2.isValid();
            const isValidRangeEnd2 = end2 && end2.isValid();

            // Check discountPrice2 first (higher priority)
            if (discount2 > 0) {
                if (isValidRangeEnd2 && now.isAfter(end2)) {
                    // discount2 expired, check discount
                } else if (isValidRangeStart2 && now.isBefore(start2)) {
                    // discount2 not started, check discount
                } else if (isValidRangeStart2 && isValidRangeEnd2) {
                    const isActive = (now.isSame(start2) || now.isAfter(start2)) && (now.isSame(end2) || now.isBefore(end2));
                    if (isActive) return true;
                } else if (isValidRangeStart2 || isValidRangeEnd2) {
                    return true;
                } else {
                    return true; // No dates, discount2 is active
                }
            }

            // Check discountPrice
            if (discount > 0) {
                if (isValidRangeEnd && now.isAfter(end)) {
                    return false;
                }
                if (isValidRangeStart && now.isBefore(start)) {
                    return false;
                }
                if (isValidRangeStart && isValidRangeEnd) {
                    return (now.isSame(start) || now.isAfter(start)) && (now.isSame(end) || now.isBefore(end));
                } else if (isValidRangeStart && !isValidRangeEnd) {
                    return now.isSame(start) || now.isAfter(start);
                } else if (!isValidRangeStart && isValidRangeEnd) {
                    return now.isSame(end) || now.isBefore(end);
                } else {
                    return true; // No dates, discount is active
                }
            }
        }

        // Fallback: if no fresh data, use simple check (but this won't validate dates)
        // This preserves existing behavior if fresh data isn't available
        return item.sale_price > 0 && item.sale_price < item.regular_price;
    };

    // Get display price (for display only - does not affect order calculations) – sipas skuid + dyqanit
    const getDisplayPrice = (item) => {
        const productDetails = getProductDetailsForItem(item);
        const sku = findSkuBySkuId(productDetails, item?.item_code);
        const priceData = getSkuPriceData(sku);
        
        if (productDetails && sku && isDiscountActive(item)) {
            const discount = Number(priceData?.discountPrice) || 0;
            const discount2 = Number(priceData?.discountPrice2) || 0;
            const regular = Number((priceData?.price ?? sku?.listPrice ?? item.regular_price) || 0);
            
            // Check discountPrice2 first (higher priority)
            if (discount2 > 0) {
                const parsePossibleMongoDate = (d) => {
                    if (!d) return null;
                    if (typeof d === "string") return dayjs(d);
                    if (d instanceof Date) return dayjs(d);
                    if (d?.$date) return dayjs(d.$date);
                    return dayjs(d);
                };
                
                const now = dayjs();
                const start2 = priceData?.fromDate2 ? parsePossibleMongoDate(priceData.fromDate2).startOf("day") : (priceData?.fromDate ? parsePossibleMongoDate(priceData.fromDate).startOf("day") : null);
                const end2 = priceData?.toDate2 ? parsePossibleMongoDate(priceData.toDate2).endOf("day") : (priceData?.toDate ? parsePossibleMongoDate(priceData.toDate).endOf("day") : null);
                
                const isValidRangeStart2 = start2 && start2.isValid();
                const isValidRangeEnd2 = end2 && end2.isValid();
                
                let isDiscount2Active = false;
                if (isValidRangeEnd2 && now.isAfter(end2)) {
                    isDiscount2Active = false;
                } else if (isValidRangeStart2 && now.isBefore(start2)) {
                    isDiscount2Active = false;
                } else if (isValidRangeStart2 && isValidRangeEnd2) {
                    isDiscount2Active = (now.isSame(start2) || now.isAfter(start2)) && (now.isSame(end2) || now.isBefore(end2));
                } else {
                    isDiscount2Active = true; // No dates, discount2 is active
                }
                
                if (isDiscount2Active) {
                    return {
                        regular: regular,
                        sale: discount,
                        sale2: discount2,
                        hasDiscount: true,
                        hasDiscount2: true
                    };
                }
            }
            
            // Check discountPrice
            if (discount > 0) {
                return {
                    regular: regular,
                    sale: discount,
                    sale2: 0,
                    hasDiscount: true,
                    hasDiscount2: false
                };
            }
        }
        
        return {
            regular: Number(item.regular_price || 0),
            sale: 0,
            sale2: 0,
            hasDiscount: false,
            hasDiscount2: false
        };
    };

    // Calculate total based on validated display prices (reactive to productPriceData and cartData)
    const validatedTotal = useMemo(() => {
        if (!cartData || cartData.length === 0) return 0;
        
        let total = 0;
        cartData.forEach((item) => {
            const displayPrice = getDisplayPrice(item);
            const itemPrice = displayPrice.hasDiscount2 && displayPrice.sale2 > 0 
                ? displayPrice.sale2 
                : (displayPrice.hasDiscount && displayPrice.sale > 0 
                    ? displayPrice.sale 
                    : displayPrice.regular);
            total += itemPrice * (item.quantity || 1);
        });
        
        return total;
    }, [cartData, productPriceData]);

    // Fetch fresh product data for display purposes only (when component mounts)
    useEffect(() => {
        const fetchFreshPriceData = async () => {
            if (!cartData || cartData.length === 0) return;

            try {
                const priceDataMap = {};
                const promises = cartData.map(async (item) => {
                    try {
                        const productId = item?._id || item?.externalId;
                        const dataId = {
                            _id: productId,
                            location: item?.location || ""
                        };
                        const res = await axios.post("/getproductbyid", dataId);
                        if (res?.data?.length > 0) {
                            const preferred =
                                (item?.location &&
                                    res.data.find(
                                        (row) =>
                                            String(row?.productDetails?.location || row?.location || "") ===
                                            String(item.location)
                                    )) ||
                                res.data[0];
                            const productDetails = preferred?.productDetails;
                            priceDataMap[`${productId}::${item?.location || ""}`] = productDetails;
                            priceDataMap[productId] = productDetails;
                        }
                    } catch (error) {
                        console.error(`Error fetching price data for ${item?._id}:`, error);
                    }
                });

                await Promise.all(promises);
                setProductPriceData(priceDataMap);

                // Re-price cart on refresh using latest DB prices (persisted cart can be stale)
                const updated = (cartData || []).map((it) => {
                    const productId = it?._id || it?.externalId;
                    const productDetails =
                        priceDataMap[`${productId}::${it?.location || ""}`] ||
                        priceDataMap[productId];
                    const p = repriceItemFromProductDetails(it, productDetails);
                    return { ...it, ...p };
                });

                if (updated.length > 0) {
                    const totalPrice = updated.reduce((acc, it) => {
                        const unit = Number(it?.sale_price || 0) > 0 ? Number(it.sale_price) : Number(it?.regular_price || 0);
                        return acc + unit * Number(it?.quantity || 1);
                    }, 0);
                    updated[0] = { ...updated[0], totalPrice: Number(totalPrice.toFixed(2)) };
                }

                dispatch(addToCart(updated));
            } catch (error) {
                console.error("Error fetching fresh price data:", error);
            }
        };

        fetchFreshPriceData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount

    const onQuantityIncrement = () => {
        setQty(qty + 1);
    };

    const onQuantityDecrement = () => {
        if (qty <= 1) return;
        setQty(qty - 1);
    };

    const remove = async (item) => {
        const sku = item?.item_code;
        const uniqueId = item?.uniqueId;
        const filteredCart = cartData.filter((it) => {
            if (uniqueId && it.uniqueId) return String(it.uniqueId) !== String(uniqueId);
            if (sku) return String(it.item_code) !== String(sku);
            return it._id !== item?._id;
        });
        dispatch(addToCart(filteredCart));
    }

    const updateCartData = (id, type, qty, item_code) => {
        let foundIndex = cartData.findIndex(element => element.item_code === item_code);

        // item_code

        if (qty > 1 || type === "plus") {
            if (type === "minus") {
                cartData[foundIndex].quantity = cartData[foundIndex].quantity - 1;
            } else {
                const stock = cartData[foundIndex].stockQty;
                if (stock > cartData[foundIndex].quantity) {
                    cartData[foundIndex].quantity = cartData[foundIndex].quantity + 1;
                }
            }
            dispatch(addToCart(cartData))
        }
    }


    return (
        <>
            <Header />
            <div className="cart-page cartpage_conatiner sm:container mx-auto mt-20">
                {/* Cart Header */}
                {cartData.length > 0 && (
                    <header className="cart-page__header">
                        <h1 className="cart-page__title">
                            Shporta
                            <svg className="cart-page__title-icon" xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M6 19m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
                                <path d="M17 19m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
                                <path d="M17 17h-11v-14h-2" />
                                <path d="M6 5l14 1l-1 7h-13" />
                            </svg>
                        </h1>
                    </header>
                )}

                {/* Layout: items left, summary right (desktop) */}
                {cartData.length > 0 && (
                    <div className="cart-page__layout">
                        {/* Cart Items List */}
                        <div className="cart-page__list">
                            {cartData.map((dtt) => (
                                <article key={dtt.uniqueId || dtt.item_code || `${dtt._id}-${dtt.size}`} className="cart-page__item">
                                    <Link to={`/productdetails/${dtt._id}/${dtt.itemCategory}`} className="cart-page__item-image-wrap">
                                        <div className="cart-page__item-image">
                                            <img src={dtt.images[0]} alt={dtt.name} />
                                        </div>
                                    </Link>
                                    <div className="cart-page__item-details">
                                        <div className="cart-page__item-top">
                                            <span className="cart-page__item-vendor">{dtt.vendor}</span>
                                            <button
                                                type="button"
                                                onClick={() => remove(dtt)}
                                                className="cart-page__item-remove"
                                                aria-label="Hiq nga shporta"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M4 4l8 8M12 4l-8 8" />
                                                </svg>
                                            </button>
                                        </div>
                                        <Link to={`/productdetails/${dtt._id}/${dtt.itemCategory}`} className="cart-page__item-name">
                                            {dtt.name?.slice(0, 35)}...
                                        </Link>
                                        {(() => {
                                            const displayPrice = getDisplayPrice(dtt);
                                            return (
                                                <div className="cart-page__item-prices">
                                                    {displayPrice.hasDiscount2 && displayPrice.sale2 > 0 ? (
                                                        <>
                                                            <span className="cart-page__price-old">{displayPrice.regular?.toFixed(2)}€</span>
                                                            {displayPrice.sale > 0 && (
                                                                <span className="cart-page__price-old">{displayPrice.sale?.toFixed(2)}€</span>
                                                            )}
                                                            <span className="cart-page__price-current">{displayPrice.sale2?.toFixed(2)}€</span>
                                                        </>
                                                    ) : displayPrice.hasDiscount && displayPrice.sale > 0 ? (
                                                        <>
                                                            <span className="cart-page__price-old">{displayPrice.regular?.toFixed(2)}€</span>
                                                            <span className="cart-page__price-current">{displayPrice.sale?.toFixed(2)}€</span>
                                                        </>
                                                    ) : (
                                                        <span className="cart-page__price-current">{displayPrice.regular?.toFixed(2)}€</span>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                        <div className="cart-page__item-meta">
                                            <span className="cart-page__item-size">
                                                <strong>Madhësia:</strong> {dtt.size}
                                            </span>
                                            <div className="cart-page__qty">
                                                <button
                                                    type="button"
                                                    onClick={() => updateCartData(dtt._id, "minus", dtt.quantity, dtt.item_code)}
                                                    aria-label="Zbrit sasinë"
                                                >
                                                    −
                                                </button>
                                                <input value={dtt.quantity} readOnly aria-label="Sasia" />
                                                <button
                                                    type="button"
                                                    onClick={() => updateCartData(dtt._id, "plus", dtt.quantity, dtt.item_code)}
                                                    aria-label="Shto sasinë"
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>

                        {/* Cart Summary (right column on desktop) */}
                        <aside className="cart-page__summary-column">
                            <div className="cart-page__summary">
                                <div className="cart-page__summary-total">
                                    <span className="cart-page__summary-label">Totali</span>
                                    <span className="cart-page__summary-amount">
                                        {validatedTotal.toFixed(2)} {process.env.REACT_APP_CURRENCY}
                                    </span>
                                    <span className="cart-page__summary-vat">Përfshirë TVSH</span>
                                </div>
                                <Link to="/checkout" className="cart-page__checkout-btn">
                                    Vazhdo me blerjen
                                </Link>
                            </div>
                            <div className="cart-page__shipping">
                            Tarifa e transportit është <strong>3€</strong> për çdo porosi.
                            </div>
                        </aside>
                    </div>
                )}

                {/* Empty Cart View */}
                {cartData.length === 0 && (
                    <div className="cart-page__empty">
                        <h1 className="cart-page__empty-title">Shporta</h1>
                        <p className="cart-page__empty-text">Ju nuk keni ende produkte në shportë.</p>
                        <Link to="/">
                            <button type="button" className="cart-page__empty-btn">Shiko koleksionet</button>
                        </Link>
                    </div>
                )}

                <div className="cart-page__spacer" />
            </div>
            <Footer />
        </>
    )
}

export default Cartpage;
